import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { formatNumber, formatTime } from '@/utils/formatters';
import { Card } from '@/components/ui/card';
import { Device, DeviceStatus, deviceStore } from '@/stores/deviceStore';
import { DeviceControlModal } from './DeviceControlModal';

interface DeviceCardProps {
  device: Device;
  mapView?: boolean;
  onControlStart?: () => void;
  onControlEnd?: () => void;
}

export const DeviceCard = observer(({ device, mapView = false, onControlStart, onControlEnd }: DeviceCardProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const status = deviceStore.deviceStatus[device.device_id] || {};
  const s = status.s || {};
  const sc = status.sc || {};
  const relay = device.relay;

  const handleOpenModal = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('Opening device control modal for device:', device.device_id);
    setIsModalOpen(true);
    
    // Dispatch event to notify map container that control is starting
    if (mapView && onControlStart) {
      onControlStart();
    } else if (mapView) {
      // For backward compatibility, dispatch a custom event
      window.dispatchEvent(new CustomEvent('device-control-start'));
    }
  };

  const handleCloseModal = () => {
    console.log('Closing device control modal for device:', device.device_id);
    setIsModalOpen(false);
    
    // Dispatch event to notify map container that control is ending
    if (mapView && onControlEnd) {
      onControlEnd();
    } else if (mapView) {
      // For backward compatibility, dispatch a custom event
      window.dispatchEvent(new CustomEvent('device-control-end'));
    }
  };

  // Handle HTML for signal strength
  const renderSignalStrength = () => {
    const csq = s.csq !== undefined ? s.csq : null;
    
    // Create signal bars (simplified version)
    if (csq === null || csq === undefined) return '--';
    
    const val = parseInt(String(csq));
    if (isNaN(val)) return '--';
    
    // Return just the number for now with an indicator
    const strengthLevel = val < 10 ? 'low' : val < 20 ? 'medium' : 'high';
    return (
      <span className={`signal-strength ${strengthLevel}`}>
        {val} <span className="signal-dot"></span>
      </span>
    );
  };

  const renderRelayState = () => {
    if (!relay || relay < 1 || relay > 3) return '--';
    
    const r = s[`r${relay}` as keyof typeof s];
    // Check connection status using the last time field
    const lastTime = s.lt || s.time;
    const isConnected = !!lastTime && (
      typeof lastTime === 'string' && 
      new Date(lastTime).getTime() > Date.now() - 10 * 60 * 1000 // 10 minutes threshold
    );
    
    return (
      <span className="flex items-center justify-center">
        {r === 1 ? (
          // ON state - green filled circle if connected, gray filled if disconnected
          <span 
            className={`block w-4 h-4 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'} shadow-sm`}
            title={isConnected ? t('dashboard.deviceDetails.on') : 'Disconnected (ON)'} 
          />
        ) : r === 0 ? (
          // OFF state - gray hollow circle with tooltip
          <span 
            className="block w-4 h-4 rounded-full bg-gray-100 border border-gray-300" 
            title={t('dashboard.deviceDetails.off')} 
          />
        ) : (
          '--'
        )}
      </span>
    );
  };

  const renderForceState = () => {
    if (!relay || relay < 1 || relay > 3) return null;
    
    const rf = s[`r${relay}f` as keyof typeof s];
    if (rf === 1) {
      return <span className="text-xs font-medium text-red-600">{t('dashboard.deviceDetails.forceOn')}</span>;
    }
    return null;
  };

  const renderSchedule = () => {
    if (!relay || relay < 1 || relay > 3) return '--';
    
    const onTime = sc[`r${relay}o` as keyof typeof sc];
    const offTime = sc[`r${relay}c` as keyof typeof sc];
    
    if (onTime && offTime) {
      return `${onTime} to ${offTime}`;
    }
    
    return '--';
  };

  return (
    <>
      <Card 
        className={`p-4 ${!mapView ? 'hover:shadow-md transition-shadow cursor-pointer' : ''}`} 
        onClick={(e) => !mapView && handleOpenModal(e)}
      >
        <div className="font-bold text-lg mb-1">{device.device_id}</div>
        
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm mb-3">
          <div><span className="font-medium">{t('dashboard.deviceDetails.relay')}:</span> {device.relay ?? '--'}</div>
          <div><span className="font-medium">{t('dashboard.deviceDetails.area')}:</span> {device.area ?? '--'}</div>
          <div><span className="font-medium">{t('dashboard.deviceDetails.point')}:</span> {device.point ?? '--'}</div>
          <div><span className="font-medium">{t('dashboard.deviceDetails.pillarNo')}:</span> {device.pillarno ?? '--'}</div>
        </div>
        
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">{t('dashboard.deviceDetails.signal')}:</span> 
            <span>{renderSignalStrength()}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-500">{t('dashboard.deviceDetails.energy')}:</span> 
            <span>{s.nrg !== undefined ? formatNumber(s.nrg) : '--'} Kw</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-500">{t('dashboard.deviceDetails.relayState')}:</span> 
            <span>{renderRelayState()}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-500">{t('dashboard.deviceDetails.schedule')}:</span> 
            <span>{renderSchedule()}</span>
          </div>
        </div>
        
        {renderForceState() && (
          <div className="mt-2">
            {renderForceState()}
          </div>
        )}
        
        <div className="mt-3 pt-2 border-t text-xs text-gray-500">
          <span className="font-medium">{t('dashboard.deviceDetails.time')}:</span> {s.lt || s.time ? formatTime(s.lt || s.time) : '--'}
        </div>
        
        {mapView && (
          <div className="mt-3 pt-2 border-t">
            <button 
              className="w-full bg-blue-500 hover:bg-blue-600 text-white py-1.5 px-3 rounded text-sm"
              onClick={handleOpenModal}
            >
              {t('deviceMap.controlDevice', 'Control Device')}
            </button>
          </div>
        )}
      </Card>
      
      {isModalOpen && (
        <DeviceControlModal 
          device={device} 
          isOpen={isModalOpen} 
          onClose={handleCloseModal}
          isMapView={mapView}
        />
      )}
    </>
  );
}); 